import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  EMISOR_TRATAMIENTO_ADMIN,
  EMISOR_TRATAMIENTO_MEDICO,
  puedeAdminDerivarMedicoEnAprobacion,
  pedidoRequiereSeleccionMedicoEnAprobacion,
  pedidoEsPami,
  resolveRecordatoriosBadgeMeta
} from '../utils/tratamientoAprobacion';
import TratamientoDosificacionCalculator from './TratamientoDosificacionCalculator';
import { buildRecetasCantidadOptions } from '../utils/tratamientoDosificacion';
import '../styles/tratamiento-aprobacion-wizard.css';

const EMISOR_OPTIONS = [
  {
    value: EMISOR_TRATAMIENTO_MEDICO,
    label: 'Médico',
    hint: 'Pedidos/Recetas'
  },
  {
    value: EMISOR_TRATAMIENTO_ADMIN,
    label: 'Administrativo',
    hint: 'Recetas a emitir'
  }
];

function EmisorTratamientoSegment({ value, onChange, disabled }) {
  return (
    <div className="crm-ta-wizard__emisor-seg" role="radiogroup" aria-label="Quién emite el tratamiento">
      {EMISOR_OPTIONS.map((opt) => {
        const selected = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={selected}
            className={`crm-ta-wizard__emisor-opt${selected ? ' is-selected' : ''}`}
            onClick={() => onChange(opt.value)}
            disabled={disabled}
          >
            <span className="crm-ta-wizard__emisor-opt-label">{opt.label}</span>
            <span className="crm-ta-wizard__emisor-opt-hint">{opt.hint}</span>
          </button>
        );
      })}
    </div>
  );
}

function WhatsAppToggle({ id, label, description, value, onChange, disabled, icon }) {
  return (
    <div className={`crm-ta-wizard__hook${value ? ' is-on' : ''}`}>
      <div className="crm-ta-wizard__hook-head">
        <span className="crm-ta-wizard__hook-icon" aria-hidden>{icon}</span>
        <div className="crm-ta-wizard__hook-copy">
          <div className="crm-ta-wizard__hook-label" id={`${id}-label`}>{label}</div>
          <p className="crm-ta-wizard__hook-desc">{description}</p>
        </div>
      </div>
      <div className="crm-ta-wizard__switch-wrap">
        <span className={`crm-ta-wizard__switch-state${value ? ' is-on' : ''}`} aria-hidden>
          {value ? 'Activo' : 'Off'}
        </span>
        <button
          id={id}
          type="button"
          role="switch"
          aria-checked={value}
          aria-labelledby={`${id}-label`}
          className={`crm-ta-wizard__switch${value ? ' is-on' : ''}`}
          onClick={() => onChange(!value)}
          disabled={disabled}
        >
          <span className="crm-ta-wizard__switch-knob" />
        </button>
      </div>
    </div>
  );
}

function formatDoctorDisplayName(nombre, genero) {
  const raw = String(nombre || '').trim();
  if (!raw) return '—';
  if (/^(dr|dra)\.?\s/i.test(raw)) return raw;
  const g = String(genero || '').trim().toLowerCase();
  const isFemaleByGenero = g.startsWith('f') || g.includes('muj');
  const isMaleByGenero = g.startsWith('m') || g.includes('hom');
  const isFemaleByName = /\b(maria|ana|paula|rocio|barbara|agustina|laura|sofia|gabriela|florencia|carla|soledad)\b/i.test(raw);
  const prefix = isFemaleByGenero || (!isMaleByGenero && isFemaleByName) ? 'Dra.' : 'Dr.';
  return `${prefix} ${raw}`;
}

function medicoInitials(nombre) {
  const parts = String(nombre || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
}

function MedicoDerivacionPicker({
  medicos,
  loading,
  disabled,
  value,
  onChange,
  search,
  onSearchChange
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const frame = requestAnimationFrame(() => {
      rootRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(frame);
  }, [open]);

  const filtered = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    let list = Array.isArray(medicos) ? medicos : [];
    if (q) {
      list = list.filter((m) => {
        const nombre = String(m.nombre || '').toLowerCase();
        const esp = String(m.especialidad || '').toLowerCase();
        const mat = String(m.matricula || '').toLowerCase();
        const shown = formatDoctorDisplayName(m.nombre, m.genero).toLowerCase();
        return nombre.includes(q) || shown.includes(q) || esp.includes(q) || mat.includes(q);
      });
    }
    if (value) {
      const hasSel = list.some((m) => String(m.id) === String(value));
      if (!hasSel) {
        const sel = (medicos || []).find((m) => String(m.id) === String(value));
        if (sel) list = [sel, ...list];
      }
    }
    return list;
  }, [medicos, search, value]);

  const selected = (medicos || []).find((m) => String(m.id) === String(value));
  const totalActivos = (medicos || []).length;
  const q = String(search || '').trim();

  return (
    <div className={`crm-ta-wizard__medico-box${open ? ' is-picker-open' : ''}`}>
      <div className="crm-ta-wizard__medico-box-head">
        <div>
          <div className="crm-ta-wizard__picker-label">Derivar a médico</div>
          <p className="crm-ta-wizard__medico-box-lead">
            Pedido sin derivación previa (p. ej. alta manual). Solo médicos activos en derivación.
          </p>
        </div>
        <span className="crm-ta-wizard__medico-count-badge">
          {loading ? '…' : `${totalActivos} activos`}
        </span>
      </div>

      <input
        type="search"
        className="crm-ta-wizard__picker-search"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder="Buscar por nombre, especialidad o matrícula…"
        disabled={disabled || loading}
        aria-label="Buscar médico"
      />

      {q ? (
        <p className="crm-ta-wizard__medico-results-hint">
          Mostrando <strong>{filtered.length}</strong> de {totalActivos} médicos
        </p>
      ) : null}

      <div ref={rootRef} className="crm-ta-wizard__medico-picker">
        <button
          type="button"
          className={`crm-ta-wizard__picker-trigger${!selected ? ' is-empty' : ''}`}
          disabled={disabled || loading}
          onClick={() => {
            if (disabled || loading) return;
            setOpen((cur) => !cur);
          }}
          aria-expanded={open}
          aria-haspopup="listbox"
        >
          {selected ? (
            <span className="crm-ta-wizard__picker-trigger-main">
              <span className="crm-ta-wizard__picker-avatar" aria-hidden>{medicoInitials(selected.nombre)}</span>
              <span className="crm-ta-wizard__picker-trigger-text">
                <span className="crm-ta-wizard__picker-trigger-name">
                  {formatDoctorDisplayName(selected.nombre, selected.genero)}
                </span>
                {selected.especialidad || selected.matricula ? (
                  <span className="crm-ta-wizard__picker-trigger-meta">
                    {[selected.especialidad, selected.matricula ? `Mat. ${selected.matricula}` : ''].filter(Boolean).join(' · ')}
                  </span>
                ) : null}
              </span>
            </span>
          ) : (
            <span className="crm-ta-wizard__picker-trigger-placeholder">
              {loading ? 'Cargando médicos…' : '— Seleccionar médico —'}
            </span>
          )}
          <span className="crm-ta-wizard__picker-chevron" aria-hidden>{open ? '▴' : '▾'}</span>
        </button>

        {open && !disabled && !loading ? (
          <div className="crm-ta-wizard__picker-menu" role="listbox">
            <button
              type="button"
              role="option"
              aria-selected={!value}
              className={`crm-ta-wizard__picker-option crm-ta-wizard__picker-option--placeholder${!value ? ' is-selected' : ''}`}
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
            >
              — Seleccionar médico —
            </button>

            {filtered.map((m) => {
              const isSelected = String(value) === String(m.id);
              const displayName = formatDoctorDisplayName(m.nombre, m.genero);
              return (
                <button
                  key={`ta-med-${m.id}`}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={`crm-ta-wizard__picker-option${isSelected ? ' is-selected' : ''}`}
                  onClick={() => {
                    onChange(String(m.id));
                    setOpen(false);
                  }}
                >
                  <span className="crm-ta-wizard__picker-avatar" aria-hidden>{medicoInitials(m.nombre)}</span>
                  <span className="crm-ta-wizard__picker-option-body">
                    <span className="crm-ta-wizard__picker-option-name">{displayName}</span>
                    <span className="crm-ta-wizard__picker-option-meta">
                      {m.especialidad || 'Sin especialidad declarada'}
                      {m.matricula ? ` · Mat. ${m.matricula}` : ''}
                    </span>
                  </span>
                  {isSelected ? <span className="crm-ta-wizard__picker-option-check" aria-hidden>✓</span> : null}
                </button>
              );
            })}

            {filtered.length === 0 ? (
              <div className="crm-ta-wizard__picker-empty">
                No hay médicos activos que coincidan con la búsqueda.
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function TratamientoAprobacionWizard({
  open,
  onClose,
  pedido,
  isMobile = false,
  isAdmin = false,
  linkedMedicoId = null,
  linkedMedicoNombre = '',
  medicosDerivacion = [],
  medicosLoading = false,
  submitting = false,
  meses,
  onMesesChange,
  fecha,
  onFechaChange,
  posologia,
  onPosologiaChange,
  cantidad,
  onCantidadChange,
  dosificacion,
  onDosificacionChange,
  emisorTratamiento,
  onEmisorTratamientoChange,
  administrativosEmision = [],
  administrativosLoading = false,
  administrativoSeleccion,
  onAdministrativoSeleccionChange,
  linkedAdministrativoNombre = '',
  webhookAlta,
  onWebhookAltaChange,
  webhookEmitir,
  onWebhookEmitirChange,
  medicoSeleccion,
  onMedicoSeleccionChange,
  medicoBuscar,
  onMedicoBuscarChange,
  onConfirm,
  parseLocalDate,
  formatDniDisplay,
  formatMedicoTitulo,
  buildMedicationDisplay
}) {
  const [step, setStep] = useState(1);

  const adminDeriva = puedeAdminDerivarMedicoEnAprobacion(pedido, isAdmin);
  const requiereMedico = pedidoRequiereSeleccionMedicoEnAprobacion(pedido);
  const totalSteps = 3;
  const badgeMeta = useMemo(
    () => resolveRecordatoriosBadgeMeta({ cantidadRecetas: cantidad }),
    [cantidad]
  );
  const esPami = pedidoEsPami(pedido);
  const cantidadNum = Math.max(0, Number(cantidad) || 0);
  const recetasOptions = useMemo(
    () => buildRecetasCantidadOptions([cantidadNum]),
    [cantidadNum]
  );
  const emisorEsAdministrativo = emisorTratamiento === EMISOR_TRATAMIENTO_ADMIN;

  const medicoDerivadoNombre = useMemo(() => {
    const id = pedido?.medico_id ? String(pedido.medico_id) : '';
    const nombre = String(pedido?.medico || pedido?.prestador_geclisa || '').trim();
    if (nombre) return formatMedicoTitulo(nombre);
    if (id) return `Médico #${id}`;
    return '';
  }, [pedido, formatMedicoTitulo]);

  const selfMedicoLabel = useMemo(() => {
    if (linkedMedicoNombre) return formatMedicoTitulo(linkedMedicoNombre);
    if (linkedMedicoId) return `Médico #${linkedMedicoId}`;
    return 'Tu usuario médico';
  }, [linkedMedicoId, linkedMedicoNombre, formatMedicoTitulo]);

  if (!open || !pedido) return null;

  const goNext = () => {
    if (step === 1) {
      if (!String(posologia || '').trim()) return;
      if (!String(cantidad || '').trim()) return;
      setStep(2);
      return;
    }
    if (step === 2) {
      setStep(3);
    }
  };

  const handleConfirm = () => {
    if (adminDeriva && !String(medicoSeleccion || '').trim()) return;
    if (requiereMedico && !adminDeriva && !linkedMedicoId) return;
    onConfirm({
      webhookAlta: Boolean(webhookAlta),
      webhookEmitir: Boolean(webhookEmitir),
      emisorTratamiento,
      administrativoSeleccion
    });
  };

  return (
    <div className="crm-ta-wizard-backdrop" role="presentation">
      <div className="crm-ta-wizard" role="dialog" aria-modal="true" aria-labelledby="crm-ta-wizard-title">
        <div className="crm-ta-wizard__chrome">
          <div className="crm-ta-wizard__head">
            <div>
              <h2 id="crm-ta-wizard-title" className="crm-ta-wizard__title">Aprobar tratamiento</h2>
              <p className="crm-ta-wizard__subtitle">Alta manual — completá el tratamiento en {totalSteps} pasos</p>
            </div>
          </div>

          <div className="crm-ta-wizard__patient">
            <div className="crm-ta-wizard__patient-main">
              <div className="crm-ta-wizard__patient-name">{pedido.paciente}</div>
              <div className="crm-ta-wizard__patient-meta">
                DNI {formatDniDisplay(pedido.dni)} · {pedido.obra_social || '—'}
              </div>
              <div className="crm-ta-wizard__patient-med">
                Medicamento: {buildMedicationDisplay(pedido).title}
              </div>
              {medicoDerivadoNombre ? (
                <div className="crm-ta-wizard__patient-meta">
                  Médico asignado: {medicoDerivadoNombre}
                </div>
              ) : null}
            </div>
            <div className="crm-ta-wizard__patient-badges">
              <span className={`crm-ta-wizard__badge crm-ta-wizard__badge--${badgeMeta.tone}`}>
                {badgeMeta.text}
              </span>
              {esPami ? (
                <span className="crm-ta-wizard__badge crm-ta-wizard__badge--pami">
                  OBRA SOCIAL PAMI
                </span>
              ) : null}
            </div>
          </div>

          <div className="crm-ta-wizard__steps crm-ta-wizard__steps--triple">
            <div className={`crm-ta-wizard__step${step === 1 ? ' is-active' : ''}${step > 1 ? ' is-done' : ''}`}>
              <span className="crm-ta-wizard__step-num">{step > 1 ? '✓' : '1'}</span>
              <span>Datos del tratamiento</span>
            </div>
            <div className={`crm-ta-wizard__step${step === 2 ? ' is-active' : ''}${step > 2 ? ' is-done' : ''}`}>
              <span className="crm-ta-wizard__step-num">{step > 2 ? '✓' : '2'}</span>
              <span>Emisión del tratamiento</span>
            </div>
            <div className={`crm-ta-wizard__step${step === 3 ? ' is-active' : ''}`}>
              <span className="crm-ta-wizard__step-num">3</span>
              <span>{adminDeriva ? 'Derivación y WhatsApp' : 'Médico y WhatsApp'}</span>
            </div>
          </div>
        </div>

        <div className="crm-ta-wizard__body">
        {step === 1 ? (
          <>
            <TratamientoDosificacionCalculator
              dosificacion={dosificacion}
              onDosificacionChange={onDosificacionChange}
              cantidadRecetas={cantidadNum || 1}
              mesesActual={meses}
              fechaInicio={fecha}
              posologia={posologia}
              onPosologiaChange={onPosologiaChange}
              onMesesChange={onMesesChange}
              onCantidadChange={onCantidadChange}
              disabled={submitting}
            />

            <div className={`crm-ta-wizard__grid crm-ta-wizard__grid--step1${isMobile ? ' crm-ta-wizard__grid--single' : ''}`}>
              <div className="crm-ta-wizard__field">
                <label htmlFor="ta-meses">Período (meses)</label>
                <input
                  id="ta-meses"
                  type="number"
                  min={1}
                  max={24}
                  value={meses}
                  onChange={(e) => onMesesChange(e.target.value)}
                  disabled={submitting}
                />
              </div>
              <div className="crm-ta-wizard__field">
                <label htmlFor="ta-fecha">Fecha de inicio</label>
                <input
                  id="ta-fecha"
                  type="date"
                  className="crm-ta-wizard__date-input"
                  value={fecha || ''}
                  onChange={(e) => onFechaChange(e.target.value)}
                  disabled={submitting}
                />
              </div>
              <div className="crm-ta-wizard__field">
                <label htmlFor="ta-posologia">Posología</label>
                <input
                  id="ta-posologia"
                  type="text"
                  value={posologia}
                  onChange={(e) => onPosologiaChange(e.target.value)}
                  placeholder="ej: 1 comprimido cada 24 horas, con el desayuno"
                  disabled={submitting}
                />
              </div>
              <div className="crm-ta-wizard__field">
                <label htmlFor="ta-cantidad">Cantidad de recetas</label>
                <select
                  id="ta-cantidad"
                  value={cantidad}
                  onChange={(e) => onCantidadChange(e.target.value)}
                  disabled={submitting}
                >
                  <option value="">Seleccionar cantidad</option>
                  {recetasOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <p className={`crm-ta-wizard__hint crm-ta-wizard__hint--info${cantidadNum > 1 ? ' crm-ta-wizard__hint--suscrito' : ' crm-ta-wizard__hint--sin-rec'}`}>
              {badgeMeta.hint}
            </p>
          </>
        ) : step === 2 ? (
          <div className="crm-ta-wizard__flow-card crm-ta-wizard__flow-card--step">
            <div className="crm-ta-wizard__flow-card-head">
              <span className="crm-ta-wizard__flow-eyebrow">Configuración del tratamiento</span>
              <h3 className="crm-ta-wizard__flow-title">Quién emite y quién administra</h3>
            </div>

            <div className="crm-ta-wizard__field crm-ta-wizard__field--emisor">
              <span className="crm-ta-wizard__field-label">¿Quién emite el tratamiento?</span>
              <EmisorTratamientoSegment
                value={emisorTratamiento}
                onChange={onEmisorTratamientoChange}
                disabled={submitting}
              />
            </div>

            {emisorEsAdministrativo ? (
              <div className="crm-ta-wizard__field crm-ta-wizard__field--admin-pick">
                <div className="crm-ta-wizard__admin-broadcast">
                  <span className="crm-ta-wizard__admin-broadcast-icon" aria-hidden="true">👥</span>
                  <div className="crm-ta-wizard__admin-broadcast-copy">
                    <strong>Emisión administrativa</strong>
                    <p className="crm-ta-wizard__hint crm-ta-wizard__hint--info">
                      La receta quedará disponible en <strong>Recetas a emitir</strong> para
                      todo el equipo administrativo. La primera persona disponible la toma y la emite.
                    </p>
                  </div>
                </div>
              </div>
            ) : null}

          </div>
        ) : (
          <>
            {adminDeriva ? (
              <MedicoDerivacionPicker
                medicos={medicosDerivacion}
                loading={medicosLoading}
                disabled={submitting}
                value={medicoSeleccion}
                onChange={onMedicoSeleccionChange}
                search={medicoBuscar}
                onSearchChange={onMedicoBuscarChange}
              />
            ) : requiereMedico ? (
              <>
                <div className="crm-ta-wizard__medico-readonly crm-ta-wizard__medico-readonly--self">
                  {selfMedicoLabel}
                </div>
                <p className="crm-ta-wizard__hint">
                  Este pedido se asignará automáticamente a tu usuario médico. No podés derivarlo a otro profesional.
                </p>
              </>
            ) : (
              <>
                <div className="crm-ta-wizard__medico-readonly">
                  {medicoDerivadoNombre || 'Sin médico'}
                </div>
                <p className="crm-ta-wizard__hint">
                  Asignado por Gestión Derivación. No se puede modificar desde la aprobación médica.
                </p>
              </>
            )}

            <div className="crm-ta-wizard__flow-card crm-ta-wizard__flow-card--step crm-ta-wizard__flow-card--whatsapp">
              <div className="crm-ta-wizard__flow-card-head">
                <span className="crm-ta-wizard__flow-eyebrow">Notificaciones</span>
                <h3 className="crm-ta-wizard__flow-title">WhatsApp al paciente</h3>
              </div>

              <p className="crm-ta-wizard__hint crm-ta-wizard__hint--info">
                <strong>Alta tratamiento</strong> se envía al confirmar la aprobación (paso 3).
                <strong> Emitir receta</strong> se envía cuando registres la emisión en Recetas a emitir.
              </p>

              <div className="crm-ta-wizard__hooks">
                <WhatsAppToggle
                  id="ta-hook-alta"
                  icon="💬"
                  label="WhatsApp · Alta tratamiento"
                  description="Mensaje al paciente con el plan aprobado (posología, período y recetas)."
                  value={webhookAlta}
                  onChange={onWebhookAltaChange}
                  disabled={submitting}
                />
                <WhatsAppToggle
                  id="ta-hook-emitir"
                  icon="📄"
                  label="WhatsApp · Emitir receta"
                  description={
                    cantidadNum > 1
                      ? `Aviso por WhatsApp al registrar cada emisión en Recetas a emitir (${cantidadNum} recetas en el plan).`
                      : 'Aviso por WhatsApp al usar «Registrar emisión» en Recetas a emitir.'
                  }
                  value={webhookEmitir}
                  onChange={onWebhookEmitirChange}
                  disabled={submitting}
                />
              </div>
            </div>
          </>
        )}

        </div>

        <div className="crm-ta-wizard__footer">
        <div className="crm-ta-wizard__actions">
          <button type="button" className="crm-ta-wizard__btn crm-ta-wizard__btn--ghost" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          {step > 1 ? (
            <button
              type="button"
              className="crm-ta-wizard__btn crm-ta-wizard__btn--ghost"
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              disabled={submitting}
            >
              ← Atrás
            </button>
          ) : null}
          {step < 3 ? (
            <button
              type="button"
              className="crm-ta-wizard__btn crm-ta-wizard__btn--primary"
              onClick={goNext}
              disabled={
                submitting
                || (step === 1 && (!String(posologia || '').trim() || !String(cantidad || '').trim()))
              }
            >
              Siguiente →
            </button>
          ) : (
            <button
              type="button"
              className="crm-ta-wizard__btn crm-ta-wizard__btn--primary"
              onClick={handleConfirm}
              disabled={
                submitting
                || (adminDeriva && !medicoSeleccion)
                || (requiereMedico && !adminDeriva && !linkedMedicoId)
              }
            >
              {submitting ? 'Procesando…' : '✓ Confirmar aprobación'}
            </button>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}
